/* eslint-disable react/prop-types */
import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { ADMIN_LIKE_ROLES, isOperationalUser } from '../config/modules';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Plus, Check, X, AlertCircle, Send, Mail, MessageCircle, Download,
  PenLine, Loader2, ChevronDown, Edit3, Ban, BarChart3, Wallet,
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DEPARTMENTS = ['Food', 'Maintenance', 'Casual Labour', 'Admin', 'Marketing', 'Others'];

const STATUS_META = {
  pending:    { label: 'Pending Approval', color: 'bg-amber-100 text-amber-800 border-amber-200' },
  approved:   { label: 'Approved',          color: 'bg-emerald-100 text-emerald-800 border-emerald-200' },
  rejected:   { label: 'Rejected',          color: 'bg-rose-100 text-rose-800 border-rose-200' },
  needs_info: { label: 'Needs More Info',   color: 'bg-sky-100 text-sky-800 border-sky-200' },
  voided:     { label: 'Voided',            color: 'bg-slate-200 text-slate-700 border-slate-300' },
};

// Format an ISO timestamp into "2h ago" / "3d ago" relative time for ops feel.
function relTime(iso) {
  if (!iso) return '—';
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return '—';
  const diffSec = Math.max(0, Math.round((Date.now() - t) / 1000));
  if (diffSec < 60) return 'just now';
  if (diffSec < 3600) return `${Math.floor(diffSec / 60)}m ago`;
  if (diffSec < 86400) return `${Math.floor(diffSec / 3600)}h ago`;
  if (diffSec < 86400 * 7) return `${Math.floor(diffSec / 86400)}d ago`;
  return new Date(iso).toISOString().slice(0, 10);
}

const fmt = (n) => `₹${Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;

// ─── Signature uploader ────────────────────────────────────────────────
function SignatureSection({ me, onUpdated }) {
  const { getHeaders } = useAuth();
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const onPick = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 1_200_000) {
      toast.error('Image is too large — keep under 1.2 MB');
      return;
    }
    setBusy(true);
    try {
      const reader = new FileReader();
      reader.onload = async () => {
        try {
          await axios.put(`${API}/me/signature`, { signature_b64: reader.result }, { headers: getHeaders() });
          toast.success('Signature saved');
          onUpdated?.();
        } catch (err) {
          toast.error(err.response?.data?.detail || 'Could not save signature');
        } finally {
          setBusy(false);
        }
      };
      reader.readAsDataURL(f);
    } catch (err) {
      toast.error('Could not read image');
      setBusy(false);
    }
  };

  return (
    <Card className="border-[#E2E8DF] shadow-sm" data-testid="my-signature-card">
      <CardHeader className="py-3 px-4 flex flex-row items-center justify-between">
        <div>
          <CardTitle className="text-sm font-semibold text-[#1A2421] flex items-center gap-1.5">
            <PenLine className="w-3.5 h-3.5" /> My Signature
          </CardTitle>
          <p className="text-[11px] text-[#5C6B64] mt-0.5">Auto-applied to vouchers you prepare or approve.</p>
        </div>
        {me?.has_signature ? (
          <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200" data-testid="signature-status-set">On file</Badge>
        ) : (
          <Badge className="bg-amber-100 text-amber-800 border-amber-200" data-testid="signature-status-missing">Not uploaded</Badge>
        )}
      </CardHeader>
      <CardContent className="px-4 pb-4 flex items-center gap-3">
        {me?.signature_b64 ? (
          <img src={me.signature_b64} alt="signature" className="h-12 max-w-[180px] object-contain border border-[#E2E8DF] rounded bg-white p-1" data-testid="signature-preview" />
        ) : (
          <div className="h-12 w-[180px] border border-dashed border-[#E2E8DF] rounded text-[10px] text-[#9CA3AF] flex items-center justify-center">No signature uploaded</div>
        )}
        <input ref={fileRef} type="file" accept="image/*" hidden onChange={onPick} data-testid="signature-file-input" />
        <Button size="sm" variant="outline" className="h-8 text-xs border-[#E2E8DF]" disabled={busy}
          onClick={() => fileRef.current?.click()} data-testid="signature-upload-btn">
          {busy ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <PenLine className="w-3 h-3 mr-1" />}
          {me?.has_signature ? 'Replace' : 'Upload'}
        </Button>
      </CardContent>
    </Card>
  );
}

// ─── Create / Edit voucher dialog ─────────────────────────────────────
function VoucherFormDialog({ open, onClose, outlets, defaultOutletId, editing, onSaved }) {
  const { getHeaders } = useAuth();
  const [outletId, setOutletId] = useState('');
  const [paidTo, setPaidTo] = useState('');
  const [amount, setAmount] = useState('');
  const [description, setDescription] = useState('');
  const [department, setDepartment] = useState(DEPARTMENTS[0]);
  const [responseNote, setResponseNote] = useState('');
  const [walletType, setWalletType] = useState('ops');  // ITER239
  const [wallets, setWallets] = useState([]);            // [{wallet_type, balance, held, available, outlet_id}]
  const [walletsLoading, setWalletsLoading] = useState(false);
  const [submitError, setSubmitError] = useState('');    // raw backend 400 (e.g. wallet short)
  const [saving, setSaving] = useState(false);
  // ITER239 · Phase 3 — id-keyed categories (with snapshot label persisted on the voucher).
  const [categories, setCategories] = useState([]);
  const [categoryId, setCategoryId] = useState('');

  const isResubmit = editing?.status === 'needs_info';
  const isEditing = !!editing;

  useEffect(() => {
    if (!open) return;
    if (editing) {
      setOutletId(editing.outlet_id);
      setPaidTo(editing.paid_to || '');
      setAmount(String(editing.amount ?? ''));
      setDescription(editing.description || '');
      setDepartment(editing.department || DEPARTMENTS[0]);
      setResponseNote('');
      setWalletType(editing.wallet_type || 'ops');
      setCategoryId(editing.category_id || '');
    } else {
      setOutletId(defaultOutletId || outlets[0]?.id || '');
      setPaidTo('');
      setAmount('');
      setDescription('');
      setDepartment(DEPARTMENTS[0]);
      setResponseNote('');
      setWalletType('ops');
      setCategoryId('');
    }
    setSubmitError('');
  }, [open, editing, defaultOutletId, outlets]);

  // ITER239 · Phase 3 — load active categories on form open.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    axios.get(`${API}/petty-cash/categories`, { headers: getHeaders() })
      .then((res) => {
        if (cancelled) return;
        const cats = res.data || [];
        setCategories(cats);
        // If editing an existing voucher whose category_id is now inactive,
        // keep showing it (don't lose attribution); else pick the first.
        if (editing?.category_id && cats.find(c => c.id === editing.category_id)) {
          setCategoryId(editing.category_id);
        } else if (!editing && cats.length && !categoryId) {
          setCategoryId(cats[0].id);
        }
      })
      .catch(() => { if (!cancelled) setCategories([]); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, editing, getHeaders]);

  // ITER239 — fetch wallet balances visible to the user (their outlets +
  // admin central). We filter client-side per selected outlet.
  useEffect(() => {
    if (!open || isEditing) {
      setWallets([]);
      return;
    }
    let cancelled = false;
    setWalletsLoading(true);
    axios.get(`${API}/petty-cash/wallets`, { headers: getHeaders() })
      .then((res) => { if (!cancelled) setWallets(res.data || []); })
      .catch(() => { if (!cancelled) setWallets([]); })
      .finally(() => { if (!cancelled) setWalletsLoading(false); });
    return () => { cancelled = true; };
  }, [open, isEditing, getHeaders]);

  const selectedOutlet = useMemo(() => outlets.find(o => o.id === outletId), [outlets, outletId]);
  const cap = selectedOutlet?.petty_cash_max_amount;
  const overCap = cap != null && Number(amount) > Number(cap);
  const noLegal = !selectedOutlet?.legal_entity_name;

  // ITER239 — resolve which wallet a Store voucher will actually draw from.
  // If the outlet has store_petty_cash_enabled=false (default), Store
  // requests fall back to the Admin central pool (outlet_id=null).
  const storeEnabledForOutlet = !!selectedOutlet?.store_petty_cash_enabled;
  const opsWallet   = useMemo(
    () => wallets.find(w => w.outlet_id === outletId && w.wallet_type === 'ops'),
    [wallets, outletId],
  );
  const storeWallet = useMemo(
    () => wallets.find(w => w.outlet_id === outletId && w.wallet_type === 'store'),
    [wallets, outletId],
  );
  const adminWallet = useMemo(
    () => wallets.find(w => w.outlet_id == null && w.wallet_type === 'admin'),
    [wallets],
  );

  // ITER314 — full list of wallets available for this outlet in the voucher
  // picker: ops + (store if enabled) + every active custom departmental wallet.
  const walletOptions = useMemo(() => {
    const opts = [
      { value: 'ops', label: 'Operations', sub: 'Outlet operational petty cash', wallet: opsWallet },
      { value: 'store', label: 'Store / Provisions',
        sub: storeEnabledForOutlet ? 'Outlet store petty cash' : 'Falls back to Admin central when outlet has no store wallet',
        wallet: storeEnabledForOutlet ? storeWallet : adminWallet,
        fallback: !storeEnabledForOutlet },
    ];
    for (const w of wallets) {
      if (w.outlet_id !== outletId) continue;
      if (['ops', 'store', 'admin'].includes(w.wallet_type)) continue;
      if (w.active === false) continue;
      opts.push({
        value: w.wallet_type,
        label: w.name || w.wallet_type,
        sub: w.description || `Departmental petty cash — ${w.name || w.wallet_type}`,
        wallet: w,
      });
    }
    return opts;
  }, [wallets, outletId, opsWallet, storeWallet, adminWallet, storeEnabledForOutlet]);

  // The effective wallet for the currently-selected wallet_type.
  const selectedOpt = walletOptions.find(o => o.value === walletType);
  const effectiveWallet = selectedOpt?.wallet;
  // sprint 377 · Canonical spendable = balance - held. Backend rejects
  // vouchers using this same rule, so client-side eligibility MUST use
  // available (not raw balance) to match server behaviour.
  const effectiveBalance = effectiveWallet?.balance ?? 0;
  const effectiveHeld = effectiveWallet?.held ?? 0;
  const effectiveAvailable = Math.max(0, Math.round((effectiveBalance - effectiveHeld) * 100) / 100);
  const effectiveLabel = walletType === 'ops'
    ? 'Operations'
    : walletType === 'store'
      ? (storeEnabledForOutlet ? 'Store' : 'Admin central (Store→Admin fallback)')
      : (selectedOpt?.label || walletType);
  const amountNum = Number(amount) || 0;
  // sprint 377 · block against Available, not Balance (matches server).
  const willBeShort = !isEditing && amountNum > 0 && amountNum > effectiveAvailable;

  const wordsPreview = useMemo(() => {
    const n = Math.floor(Number(amount) || 0);
    if (!n) return '';
    return amountToWordsIndian(n);
  }, [amount]);

  const submit = async () => {
    if (!outletId) return toast.error('Select an outlet');
    if (!paidTo.trim()) return toast.error('Enter Paid To');
    if (!Number(amount) || Number(amount) <= 0) return toast.error('Enter a valid amount');
    if (!description.trim()) return toast.error('Enter description');
    if (!categoryId) return toast.error('Select a category');
    if (isResubmit && !responseNote.trim()) return toast.error('Please add a response / clarification');
    setSubmitError('');
    setSaving(true);
    // Keep department aligned with the selected category label for legacy reporting/PDF.
    const selectedCat = categories.find(c => c.id === categoryId);
    const effDepartment = selectedCat?.label || department || DEPARTMENTS[0];
    try {
      if (editing) {
        const body = {
          paid_to: paidTo.trim(), amount: Number(amount),
          description: description.trim(), department: effDepartment,
          category_id: categoryId || null,
        };
        if (isResubmit) body.response_note = responseNote.trim();
        await axios.put(`${API}/petty-cash/${editing.id}`, body, { headers: getHeaders() });
        toast.success(isResubmit ? 'Re-submitted for approval' : 'Voucher updated');
      } else {
        await axios.post(`${API}/petty-cash`, {
          outlet_id: outletId, paid_to: paidTo.trim(), amount: Number(amount),
          description: description.trim(), department: effDepartment,
          wallet_type: walletType,  // ITER239
          category_id: categoryId || null,  // ITER239 · Phase 3
        }, { headers: getHeaders() });
        toast.success('Voucher created');
      }
      onSaved?.();
      onClose();
    } catch (err) {
      const detail = err.response?.data?.detail || 'Could not save voucher';
      // Surface backend wallet-short message verbatim in the dialog itself
      // (in addition to the toast) so the user sees the precise blocker
      // and the suggested transfer/replenishment path.
      setSubmitError(detail);
      toast.error(detail);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md max-h-[90vh] flex flex-col gap-0 p-0" data-testid="voucher-dialog">
        <DialogHeader className="px-6 pt-6 pb-2 shrink-0 border-b border-[#E2E8DF]/60">
          <DialogTitle className="text-[#1A2421]">{editing ? 'Edit Voucher' : 'Create Petty Cash Voucher'}</DialogTitle>
          <DialogDescription className="text-[#5C6B64]">
            {editing?.status === 'needs_info'
              ? 'Update the details below — submitting will re-send for approval.'
              : 'Enter the expense details. Auto-numbered upon save.'}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 px-6 py-4 overflow-y-auto flex-1 min-h-0">
          {!editing && (
            <div className="space-y-1.5">
              <Label className="text-sm text-[#5C6B64]">Outlet <span className="text-[#C45A41]">*</span></Label>
              <Select value={outletId} onValueChange={setOutletId} disabled={!!editing}>
                <SelectTrigger className="h-10 border-[#E2E8DF]" data-testid="voucher-outlet-select"><SelectValue placeholder="Choose outlet" /></SelectTrigger>
                <SelectContent>
                  {outlets.map(o => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}
                </SelectContent>
              </Select>
              {selectedOutlet && (
                <p className="text-[10px] text-[#5C6B64]">
                  {selectedOutlet.legal_entity_name || <span className="text-[#C45A41]">Legal entity name not set — configure under Organization → Outlets.</span>}
                </p>
              )}
            </div>
          )}
          {!editing && outletId && (
            <div className="space-y-1.5" data-testid="voucher-wallet-block">
              <Label className="text-sm text-[#5C6B64] flex items-center gap-1.5">
                <Wallet className="w-3.5 h-3.5" /> Draw from wallet <span className="text-[#C45A41]">*</span>
              </Label>
              <div className="grid grid-cols-2 gap-2">
                {walletOptions.map((opt) => {
                  const isSelected = walletType === opt.value;
                  const w = opt.wallet;
                  const bal = w?.balance ?? 0;
                  const heldW = w?.held ?? 0;
                  const availW = Math.max(0, Math.round((bal - heldW) * 100) / 100);
                  return (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => { setWalletType(opt.value); setSubmitError(''); }}
                      data-testid={`voucher-wallet-${opt.value}`}
                      className={`text-left rounded border px-3 py-2 transition ${isSelected
                        ? 'border-[color:var(--color-primary)] bg-[color:var(--color-primary)]/[0.06]'
                        : 'border-[#E2E8DF] bg-white hover:bg-[#F9F9F8]'}`}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-[12px] font-semibold text-[#1A2421]">{opt.label}</span>
                        <span className={`text-[11px] tabular-nums font-mono ${availW > 0 ? 'text-emerald-700' : 'text-rose-700'}`}
                          data-testid={`voucher-wallet-${opt.value}-available`}>
                          {walletsLoading ? '…' : fmt(availW)}
                        </span>
                      </div>
                      {heldW > 0 && (
                        <p className="text-[10px] text-[#7A8B82] mt-0.5 font-mono tabular-nums"
                            data-testid={`voucher-wallet-${opt.value}-reserved`}>
                          Balance {fmt(bal)} · Reserved {fmt(heldW)}
                        </p>
                      )}
                      <p className="text-[10px] text-[#5C6B64] mt-0.5 leading-snug line-clamp-2">
                        {opt.fallback ? 'Store wallet not enabled — will draw from Admin central pool.' : opt.sub}
                      </p>
                    </button>
                  );
                })}
              </div>
              <p className="text-[10px] text-[#5C6B64]">
                Selected: <strong className="text-[#1A2421]">{effectiveLabel}</strong> ·
                Available <span className="font-mono tabular-nums" data-testid="voucher-selected-available">{walletsLoading ? '…' : fmt(effectiveAvailable)}</span>
                {effectiveHeld > 0 && (
                  <span className="ml-1 text-[#7A8B82]">
                    (Balance <span className="font-mono tabular-nums">{fmt(effectiveBalance)}</span> · Reserved <span className="font-mono tabular-nums">{fmt(effectiveHeld)}</span>)
                  </span>
                )}
                {willBeShort && (
                  <span className="text-[#C45A41] ml-1" data-testid="voucher-short-warning">— amount exceeds available (balance minus reserved).</span>
                )}
              </p>
            </div>
          )}
          <div className="space-y-1.5">
            <Label className="text-sm text-[#5C6B64]">Paid To <span className="text-[#C45A41]">*</span></Label>
            <Input value={paidTo} onChange={e => setPaidTo(e.target.value)} placeholder="Vendor / individual name"
              className="h-10 bg-[#F9F9F8] border-[#E2E8DF]" data-testid="voucher-paid-to" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-sm text-[#5C6B64]">
              Amount (₹) <span className="text-[#C45A41]">*</span>
              {cap != null && <span className="ml-2 text-[10px] text-[#5C6B64]">Max ₹{cap.toLocaleString('en-IN')}</span>}
            </Label>
            <Input type="number" value={amount} onChange={e => setAmount(e.target.value)} placeholder="0"
              className={`h-10 bg-[#F9F9F8] border-[#E2E8DF] ${overCap ? 'border-[#C45A41] focus:border-[#C45A41]' : ''}`}
              data-testid="voucher-amount" />
            {overCap && <p className="text-[11px] text-[#C45A41]">Amount exceeds the outlet limit.</p>}
          </div>
          {wordsPreview && (
            <div className="space-y-1.5">
              <Label className="text-sm text-[#5C6B64]">Amount in Words <span className="text-[#9CA3AF]">(auto)</span></Label>
              <div className="px-3 py-2 rounded border border-[#E2E8DF] bg-[#F9F9F8] text-[12px] text-[#1A2421]" data-testid="voucher-amount-words">
                {wordsPreview}
              </div>
            </div>
          )}
          <div className="space-y-1.5">
            <Label className="text-sm text-[#5C6B64]">Expense Description <span className="text-[#C45A41]">*</span></Label>
            <Textarea rows={2} value={description} onChange={e => setDescription(e.target.value)}
              placeholder="What this expense is for"
              className="bg-[#F9F9F8] border-[#E2E8DF]" data-testid="voucher-description" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-sm text-[#5C6B64]">Category <span className="text-[#C45A41]">*</span></Label>
            <Select value={categoryId} onValueChange={setCategoryId}>
              <SelectTrigger className="h-10 border-[#E2E8DF]" data-testid="voucher-category">
                <SelectValue placeholder="Select category" />
              </SelectTrigger>
              <SelectContent>
                {categories.map(c => (
                  <SelectItem key={c.id} value={c.id} data-testid={`voucher-category-opt-${c.id}`}>
                    {c.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-[10px] text-[#5C6B64]">
              Used for reporting only — not printed on the voucher. Admins can edit the list under Wallets → Settings.
            </p>
          </div>
          {isResubmit && (
            <div className="rounded border border-amber-300 bg-amber-50 px-3 py-2 space-y-2" data-testid="needs-info-block">
              <div className="text-[11px]">
                <p className="text-amber-900 font-semibold flex items-center gap-1">
                  <AlertCircle className="w-3 h-3" /> Approver requested more information
                </p>
                {editing.info_requested_by?.name && (
                  <p className="text-[10px] text-amber-800/80 mt-0.5">
                    {editing.info_requested_by.name} · {editing.info_requested_at?.slice(0, 16).replace('T', ' ')}
                  </p>
                )}
                <p className="text-[12px] text-amber-900 mt-1 whitespace-pre-wrap">{editing.info_request_note}</p>
              </div>
              <div className="space-y-1">
                <Label className="text-[11px] text-amber-900 font-medium">
                  Your Response / Clarification <span className="text-[#C45A41]">*</span>
                </Label>
                <Textarea rows={3} value={responseNote} onChange={e => setResponseNote(e.target.value)}
                  placeholder="Add the details requested. This will be visible to the approver."
                  className="bg-white border-amber-300 focus:border-amber-500 text-[12px]"
                  data-testid="voucher-response-note" />
              </div>
            </div>
          )}
          {submitError && (
            <div
              className="rounded border border-[#C45A41]/40 bg-[#C45A41]/[0.06] px-3 py-2 text-[12px] text-[#7A2E1F]"
              data-testid="voucher-submit-error">
              <p className="font-semibold flex items-center gap-1 mb-0.5">
                <AlertCircle className="w-3.5 h-3.5" /> Cannot create voucher
              </p>
              <p className="leading-snug whitespace-pre-wrap">{submitError}</p>
            </div>
          )}
        </div>
        <DialogFooter className="px-6 py-4 border-t border-[#E2E8DF] shrink-0 bg-white">
          <Button variant="outline" onClick={onClose} className="border-[#E2E8DF] text-[#5C6B64]" data-testid="voucher-cancel">Cancel</Button>
          <Button disabled={saving || overCap || noLegal} onClick={submit}
            className="bg-[color:var(--color-primary)] hover:bg-[color:var(--color-primary-hover)] text-white" data-testid="voucher-save">
            {saving ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : null}
            {editing ? (isResubmit ? 'Respond & Resubmit' : 'Update') : 'Create Voucher'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Approval action dialog (reject / request-info) ───────────────────
function ActionDialog({ open, onClose, mode, voucher, onDone }) {
  const { getHeaders } = useAuth();
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setNotes(''); }, [open]);

  const submit = async () => {
    if (!notes.trim()) {
      toast.error(mode === 'reject' ? 'Reason is required' : 'Please specify what info is needed');
      return;
    }
    setBusy(true);
    try {
      const path = mode === 'reject' ? 'reject' : 'request-info';
      await axios.post(`${API}/petty-cash/${voucher.id}/${path}`, { notes: notes.trim() }, { headers: getHeaders() });
      toast.success(mode === 'reject' ? 'Voucher rejected' : 'Info requested');
      onDone?.();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Action failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-sm" data-testid={`action-dialog-${mode}`}>
        <DialogHeader>
          <DialogTitle>{mode === 'reject' ? 'Reject Voucher' : 'Ask for More Info'}</DialogTitle>
          <DialogDescription className="text-[#5C6B64]">
            {voucher?.voucher_no} · {fmt(voucher?.amount)} · {voucher?.paid_to}
          </DialogDescription>
        </DialogHeader>
        <Textarea rows={3} value={notes} onChange={e => setNotes(e.target.value)}
          placeholder={mode === 'reject' ? 'Reason for rejection' : 'What additional information is needed?'}
          className="bg-[#F9F9F8] border-[#E2E8DF]" data-testid="action-notes" />
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="border-[#E2E8DF] text-[#5C6B64]">Cancel</Button>
          <Button disabled={busy} onClick={submit}
            className={mode === 'reject' ? 'bg-rose-600 hover:bg-rose-700 text-white' : 'bg-sky-600 hover:bg-sky-700 text-white'}
            data-testid="action-submit">
            {busy ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : null}
            {mode === 'reject' ? 'Reject' : 'Send Request'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Void dialog (ITER239 — admin-only; replaces hard delete) ─────────
// Two paths are visualised: voiding a pending/needs_info voucher releases
// the hold; voiding an approved voucher credits the cash back. Both
// require a free-text reason and append an immutable ledger entry.
function VoidDialog({ open, onClose, voucher, onDone }) {
  const { getHeaders } = useAuth();
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) { setReason(''); setBusy(false); } }, [open]);

  if (!voucher) return null;
  const isApproved = voucher.status === 'approved';
  const effectLabel = isApproved
    ? 'cash credited back to the wallet'
    : 'reserved hold released back to the wallet';

  const submit = async () => {
    if (!reason.trim()) {
      toast.error('Void reason is required');
      return;
    }
    setBusy(true);
    try {
      await axios.delete(`${API}/petty-cash/${voucher.id}`, {
        headers: getHeaders(),
        data: { reason: reason.trim() },
      });
      toast.success(isApproved ? 'Voucher voided — cash credited back' : 'Voucher voided — hold released');
      onDone?.();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not void voucher');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-sm" data-testid="void-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-[#1A2421]">
            <Ban className="w-4 h-4 text-rose-600" /> Void Voucher
          </DialogTitle>
          <DialogDescription className="text-[#5C6B64]">
            {voucher.voucher_no} · {fmt(voucher.amount)} · {voucher.paid_to}
          </DialogDescription>
        </DialogHeader>
        <div className="rounded border border-amber-200 bg-amber-50 px-3 py-2 text-[11px] text-amber-900">
          <p className="font-semibold">This cannot be undone.</p>
          <p className="mt-0.5 leading-snug">
            The voucher will move to <strong>VOIDED</strong>{' '}
            (from <strong>{(voucher.status || '').toUpperCase().replace('_', ' ')}</strong>),
            the {effectLabel}, and a permanent ledger entry will record who, when, and why.
          </p>
        </div>
        <div className="space-y-1.5">
          <Label className="text-sm text-[#5C6B64]">Reason for void <span className="text-[#C45A41]">*</span></Label>
          <Textarea
            rows={3}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="e.g. Vendor refunded, duplicate entry, wrong outlet…"
            className="bg-[#F9F9F8] border-[#E2E8DF] text-[12px]"
            data-testid="void-reason" />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="border-[#E2E8DF] text-[#5C6B64]" data-testid="void-cancel">Cancel</Button>
          <Button
            disabled={busy || !reason.trim()}
            onClick={submit}
            className="bg-rose-600 hover:bg-rose-700 text-white"
            data-testid="void-submit">
            {busy ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Ban className="w-3 h-3 mr-1" />}
            Void Voucher
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Detail / history dialog ──────────────────────────────────────────
function VoucherDetailDialog({ voucher, onClose }) {
  if (!voucher) return null;
  const meta = STATUS_META[voucher.status];
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto" data-testid="voucher-detail-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-[#1A2421]">
            <span className="font-mono text-[14px] font-semibold text-[color:var(--color-primary)] bg-[color:var(--color-primary)]/[0.08] border border-[color:var(--color-primary)]/20 rounded px-2 py-0.5">{voucher.voucher_no}</span>
            <Badge className={`${meta.color} text-[10px]`}>{meta.label}</Badge>
          </DialogTitle>
          <DialogDescription className="text-[#5C6B64]">{voucher.outlet_name} · {voucher.legal_entity_name}</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3 text-sm">
          <Field label="Paid To" value={voucher.paid_to} />
          <Field label="Amount" value={fmt(voucher.amount)} />
          <Field label="Department" value={voucher.department} />
          <Field label="Created" value={voucher.created_at ? `${voucher.created_at.slice(0, 16).replace('T', ' ')} (${relTime(voucher.created_at)})` : '—'} />
          {voucher.approved_at && (
            <Field label="Approved" value={`${voucher.approved_at.slice(0, 16).replace('T', ' ')} (${relTime(voucher.approved_at)})`} />
          )}
          <Field label="Last Updated" value={voucher.updated_at ? `${voucher.updated_at.slice(0, 16).replace('T', ' ')} (${relTime(voucher.updated_at)})` : '—'} />
          <div className="col-span-2"><Field label="Description" value={voucher.description} /></div>
          <div className="col-span-2"><Field label="Amount in Words" value={voucher.amount_in_words} /></div>
        </div>
        <div className="grid grid-cols-2 gap-4 mt-2 pt-3 border-t border-[#E2E8DF]">
          <SigBlock label="Prepared By" person={voucher.prepared_by} />
          <SigBlock label="Approved By" person={voucher.approved_by} pending={voucher.status !== 'approved'} />
        </div>
        {voucher.history && voucher.history.length > 0 && (
          <div className="space-y-2 mt-3 pt-3 border-t border-[#E2E8DF]" data-testid="voucher-timeline">
            <p className="text-[10px] uppercase tracking-wider text-[#5C6B64] font-medium">Timeline</p>
            <ConversationTimeline history={voucher.history} />
          </div>
        )}
        {voucher.rejection_reason && (
          <p className="text-[12px] text-[#C45A41] bg-[#C45A41]/5 px-3 py-2 rounded border border-[#C45A41]/20">
            <strong>Rejected:</strong> {voucher.rejection_reason}
          </p>
        )}
        {voucher.status === 'voided' && (
          <div className="mt-2 rounded border border-slate-300 bg-slate-50 px-3 py-2 text-[12px] text-slate-800" data-testid="void-summary">
            <p className="font-semibold flex items-center gap-1">
              <Ban className="w-3.5 h-3.5 text-rose-600" /> Voided
              {voucher.prev_status && (
                <span className="ml-1 text-[10px] uppercase tracking-wider text-slate-500">
                  (from {voucher.prev_status.replace('_', ' ')})
                </span>
              )}
            </p>
            <p className="mt-0.5 leading-snug">
              <strong>Reason:</strong> {voucher.void_reason || '—'}
            </p>
            {voucher.voided_by && (
              <p className="mt-0.5 text-[10px] text-slate-600 tabular-nums">
                {voucher.voided_by.name} · {(voucher.voided_at || '').slice(0, 16).replace('T', ' ')}
              </p>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

/* ─── Conversation Timeline ───────────────────────────────────────
   Renders the full append-only history array as a chat-style log.
   Each action carries a side (Manager left vs Approver right), an
   accent colour, and the note (if any). New entries always append —
   nothing is ever overwritten — so multiple needs_info → respond loops
   render correctly as a single conversation. */
const ACTION_META = {
  created:     { side: 'manager',  label: 'Created',         color: 'bg-[color:var(--color-primary)]/[0.06] border-[color:var(--color-primary)]/20 text-[color:var(--color-primary)]' },
  edited:      { side: 'manager',  label: 'Edited',          color: 'bg-slate-50 border-slate-200 text-slate-700' },
  needs_info:  { side: 'approver', label: 'Requested Info',  color: 'bg-amber-50 border-amber-200 text-amber-900' },
  resubmitted: { side: 'manager',  label: 'Responded & Resubmitted', color: 'bg-emerald-50 border-emerald-200 text-emerald-900' },
  approved:    { side: 'approver', label: 'Approved',        color: 'bg-emerald-50 border-emerald-200 text-emerald-900' },
  rejected:    { side: 'approver', label: 'Rejected',        color: 'bg-rose-50 border-rose-200 text-rose-900' },
  voided:      { side: 'approver', label: 'Voided',          color: 'bg-slate-100 border-slate-300 text-slate-800' },
};

function ConversationTimeline({ history }) {
  if (!history?.length) return null;
  return (
    <ol className="space-y-1.5" data-testid="conversation-timeline">
      {history.map((h, i) => {
        const meta = ACTION_META[h.action] || { side: 'manager', label: h.action, color: 'bg-slate-50 border-slate-200 text-slate-700' };
        const isApprover = meta.side === 'approver';
        return (
          <li key={i} className={`flex ${isApprover ? 'justify-end' : 'justify-start'}`} data-testid={`tl-${i}-${h.action}`}>
            <div className={`max-w-[78%] rounded-lg border px-3 py-2 ${meta.color}`}>
              <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-semibold opacity-90">
                <span>{isApprover ? '✓ Approver' : '✎ Manager'}</span>
                <span className="opacity-60">·</span>
                <span>{meta.label}</span>
              </div>
              <div className="text-[10px] opacity-70 mt-0.5 tabular-nums">
                {h.by ? `${h.by} · ` : ''}{(h.at || '').slice(0, 16).replace('T', ' ')}
              </div>
              {h.notes && (
                <p className="text-[12px] mt-1 whitespace-pre-wrap leading-snug">{h.notes}</p>
              )}
            </div>
          </li>
        );
      })}
    </ol>
  );
}

const Field = ({ label, value }) => (
  <div>
    <p className="text-[10px] uppercase tracking-wider text-[#5C6B64] mb-0.5">{label}</p>
    <p className="text-[13px] text-[#1A2421]">{value || '—'}</p>
  </div>
);

const SigBlock = ({ label, person, pending }) => (
  <div className="text-center">
    {person?.signature
      ? <img src={person.signature} alt={`${label} signature`} className="h-12 mx-auto object-contain" />
      : <div className="h-12 flex items-center justify-center text-[10px] text-[#9CA3AF] italic">{pending ? '(pending)' : '—'}</div>}
    <div className="border-t border-[#1A2421]/30 pt-1 mt-1">
      <p className="text-[12px] font-medium text-[#1A2421]">{person?.name || '—'}</p>
      <p className="text-[9px] uppercase tracking-wider text-[#5C6B64]">{label}</p>
    </div>
  </div>
);

// ─── Main page ────────────────────────────────────────────────────────
export default function PettyCashModule() {
  const { user, getHeaders } = useAuth();
  const [me, setMe] = useState(null);
  const [outlets, setOutlets] = useState([]);
  const [vouchers, setVouchers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('all');
  // iter180p4 — keyset pagination + server-side filter
  const PAGE_SIZE = 50;
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [counts, setCounts] = useState({ all: 0, pending: 0, approved: 0, rejected: 0, needs_info: 0 });
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(null);
  const [actionMode, setActionMode] = useState(null); // 'reject' | 'request-info'
  const [actionTarget, setActionTarget] = useState(null);
  const [voidTarget, setVoidTarget] = useState(null);   // ITER239 — void confirmation target
  const [detail, setDetail] = useState(null);

  const canApprove = ADMIN_LIKE_ROLES.includes(user?.role);
  // Creator/approver split — backend enforces the same rule. Every operational
  // (manager-shell) user can raise a voucher. Admins approve, they don't create.
  // accountant gets the manager view too — they raise reimbursement requests for
  // their team and approval still rolls up to area_manager/admin.
  const canCreate = isOperationalUser(user);
  const isManager = isOperationalUser(user);

  const reloadMe = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/me`, { headers: getHeaders() });
      setMe(res.data);
    } catch { /* noop */ }
  }, [getHeaders]);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      // iter180p4 — server-side status filter + bounded page size.
      // Approval page now fetches ONLY the active tab's vouchers
      // (not all 200 like before). Counts are pulled in parallel from
      // a cheap pending-count endpoint + lightweight per-status calls.
      // ITER239 · Phase 4 — 'archive' tab pulls terminal vouchers >30d.
      const statusParam = tab === 'all' || tab === 'archive' ? '' : `&status=${tab}`;
      const params = new URLSearchParams();
      if (tab !== 'all' && tab !== 'archive') params.set('status', tab);
      if (tab === 'archive') params.set('archived', 'true');
      params.set('limit', String(PAGE_SIZE));

      const [oRes, vRes, cRes] = await Promise.all([
        axios.get(`${API}/admin/outlets`, { headers: getHeaders() })
          .catch(() => axios.get(`${API}/outlets`, { headers: getHeaders() })),
        axios.get(`${API}/petty-cash?${params.toString()}`, { headers: getHeaders() }),
        axios.get(`${API}/petty-cash/pending-count`, { headers: getHeaders() })
          .catch(() => ({ data: { count: 0 } })),
      ]);
      const allOutlets = (oRes.data || []).filter(o => (o.status || 'active') === 'active');
      const visibleOutlets = isManager
        ? allOutlets.filter(o => (user?.assigned_outlet_ids || [user?.outlet_id]).includes(o.id))
        : allOutlets;
      setOutlets(visibleOutlets);
      const list = vRes.data || [];
      setVouchers(list);
      setHasMore(list.length === PAGE_SIZE);
      // Counts: only pending is server-authoritative (cheap count_documents);
      // others approximate from the visible page until user navigates the tab.
      setCounts(c => ({
        ...c,
        [tab === 'all' ? 'all' : tab]: list.length + (list.length === PAGE_SIZE ? 0 : 0),
        pending: cRes.data?.count ?? c.pending,
      }));
      window.dispatchEvent(new Event('petty-cash-updated'));
      // suppress unused statusParam (kept for grep visibility)
      void statusParam;
    } catch (err) {
      toast.error('Could not load vouchers');
    } finally {
      setLoading(false);
    }
  }, [getHeaders, isManager, user, tab]);

  const loadMore = useCallback(async () => {
    if (!vouchers.length || loadingMore) return;
    setLoadingMore(true);
    try {
      const last = vouchers[vouchers.length - 1];
      const params = new URLSearchParams();
      if (tab !== 'all' && tab !== 'archive') params.set('status', tab);
      if (tab === 'archive') params.set('archived', 'true');
      params.set('limit', String(PAGE_SIZE));
      if (last?.created_at) params.set('cursor', last.created_at);
      const res = await axios.get(
        `${API}/petty-cash?${params.toString()}`,
        { headers: getHeaders() }
      );
      const more = res.data || [];
      setVouchers(prev => [...prev, ...more]);
      setHasMore(more.length === PAGE_SIZE);
    } catch {
      toast.error('Could not load more vouchers');
    } finally {
      setLoadingMore(false);
    }
  }, [vouchers, loadingMore, tab, getHeaders]);

  useEffect(() => { reloadMe(); }, [reloadMe]);
  useEffect(() => { reload(); }, [reload]);

  const filtered = vouchers;  // iter180p4 — server filters now

  const approve = async (v) => {
    try {
      await axios.post(`${API}/petty-cash/${v.id}/approve`, {}, { headers: getHeaders() });
      toast.success('Voucher approved');
      reload();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not approve');
    }
  };

  const downloadPdf = async (v) => {
    try {
      const res = await axios.get(`${API}/petty-cash/${v.id}/pdf`, {
        headers: getHeaders(), responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url; a.download = `${v.voucher_no}.pdf`; a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error('Could not download PDF');
    }
  };

  /**
   * Fetch the voucher PDF blob from the API. Returns a File so we can attach it
   * via the Web Share Level-2 API on mobile / supported desktops.
   */
  const fetchVoucherFile = async (v) => {
    const res = await axios.get(`${API}/petty-cash/${v.id}/pdf`, {
      headers: getHeaders(), responseType: 'blob',
    });
    const filename = `${v.voucher_no}.pdf`;
    return new File([res.data], filename, { type: 'application/pdf' });
  };

  /** Trigger a browser download for the same file (fallback when Share API
   *  refuses or when the user picks an app that requires manual attachment). */
  const triggerDownload = (file) => {
    const url = window.URL.createObjectURL(file);
    const a = document.createElement('a');
    a.href = url; a.download = file.name; a.click();
    window.URL.revokeObjectURL(url);
  };

  const sendEmail = async (v) => {
    const outlet = outlets.find(o => o.id === v.outlet_id);
    const to = outlet?.accounts_email || '';
    if (!to) {
      toast.error('Accounts email not configured for this outlet');
      return;
    }
    const subject = `Petty Cash Voucher – ${v.voucher_no}`;
    const body = `Please find attached Petty Cash Voucher No: ${v.voucher_no} for your reference.\n\nRegards,\n${v.outlet_name || ''}`.trim();
    try {
      const file = await fetchVoucherFile(v);
      // Native share with attachment (mobile + modern desktop)
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        try {
          await navigator.share({ files: [file], title: subject, text: body });
          toast.success('Shared');
          return;
        } catch (e) {
          // user cancelled — fall through to mailto
          if (e?.name === 'AbortError') return;
        }
      }
      // Fallback: download the PDF + open the email composer.
      triggerDownload(file);
      window.location.href = `mailto:${encodeURIComponent(to)}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
      toast.info('Voucher PDF downloaded — attach it to the email window that just opened.');
    } catch {
      toast.error('Could not generate voucher PDF');
    }
  };

  const sendWhatsApp = async (v) => {
    const outlet = outlets.find(o => o.id === v.outlet_id);
    const number = (outlet?.accounts_whatsapp || '').replace(/[^0-9]/g, '');
    if (!number) {
      toast.error('Accounts WhatsApp number not configured for this outlet');
      return;
    }
    const caption = `Please find attached Petty Cash Voucher No: ${v.voucher_no}.`;
    try {
      const file = await fetchVoucherFile(v);
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        try {
          await navigator.share({ files: [file], title: `Petty Cash Voucher ${v.voucher_no}`, text: caption });
          toast.success('Shared');
          return;
        } catch (e) {
          if (e?.name === 'AbortError') return;
        }
      }
      // Fallback (desktop): WhatsApp Web doesn't accept attachments via deep
      // link, so we download the PDF and open the chat — user drags the file in.
      triggerDownload(file);
      window.open(`https://wa.me/${number}?text=${encodeURIComponent(caption)}`, '_blank');
      toast.info('Voucher PDF downloaded — drag it into the WhatsApp chat that just opened.');
    } catch {
      toast.error('Could not generate voucher PDF');
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-lg sm:text-xl font-semibold tracking-tight text-[#1A2421]">Petty Cash</h1>
          <p className="text-[12px] text-[#5C6B64]">Digital vouchers · approval discipline · audit-ready</p>
        </div>
        <div className="flex items-center gap-2">
          <Link
            to={user?.role === 'admin' || user?.role === 'super_admin' || user?.role === 'area_manager'
                ? '/admin/petty-cash/wallets' : '/manager/petty-cash/wallets'}
            className="h-8 text-xs gap-1 inline-flex items-center px-3 rounded border border-[#E2E8DF] bg-white text-[#1A2421] hover:bg-[#F4F6F2]"
            data-testid="open-wallets-link">
            <Wallet className="w-3.5 h-3.5" /> Wallets & Transfers
          </Link>
          <Link
            to={user?.role === 'admin' || user?.role === 'super_admin' || user?.role === 'area_manager'
                ? '/admin/petty-cash/reports' : '/manager/petty-cash/reports'}
            className="h-8 text-xs gap-1 inline-flex items-center px-3 rounded border border-[#E2E8DF] bg-white text-[#1A2421] hover:bg-[#F4F6F2]"
            data-testid="open-reports-link">
            <BarChart3 className="w-3.5 h-3.5" /> Reports
          </Link>
          {canCreate && (
            <Button onClick={() => { setEditing(null); setCreating(true); }}
              className="bg-[color:var(--color-primary)] hover:bg-[color:var(--color-primary-hover)] text-white h-8 text-xs gap-1" data-testid="create-voucher-btn">
              <Plus className="w-3.5 h-3.5" /> Create Petty Cash Voucher
            </Button>
          )}
        </div>
      </div>

      {/* Signature management is shown ONLY to approvers (admin / area_manager
         / super_admin). Managers do not upload or manage signatures — the
         "Prepared By" field on a voucher uses only the manager's name. */}
      {canApprove && <SignatureSection me={me} onUpdated={reloadMe} />}

      <Card className="border-[#E2E8DF] shadow-sm">
        <CardHeader className="py-2.5 px-4 border-b border-[#E2E8DF]">
          <Tabs value={tab} onValueChange={setTab}>
            <TabsList className="bg-[#F3F5F2] h-8">
              <TabsTrigger value="all" className="text-[11px] h-6 px-2.5" data-testid="tab-all">All ({counts.all})</TabsTrigger>
              <TabsTrigger value="pending" className="text-[11px] h-6 px-2.5" data-testid="tab-pending">Pending ({counts.pending})</TabsTrigger>
              <TabsTrigger value="approved" className="text-[11px] h-6 px-2.5" data-testid="tab-approved">Approved ({counts.approved})</TabsTrigger>
              <TabsTrigger value="rejected" className="text-[11px] h-6 px-2.5" data-testid="tab-rejected">Rejected ({counts.rejected})</TabsTrigger>
              <TabsTrigger value="needs_info" className="text-[11px] h-6 px-2.5" data-testid="tab-needs-info">Needs Info ({counts.needs_info})</TabsTrigger>
              <TabsTrigger value="voided" className="text-[11px] h-6 px-2.5" data-testid="tab-voided">Voided</TabsTrigger>
              <TabsTrigger value="archive" className="text-[11px] h-6 px-2.5" data-testid="tab-archive">Archive</TabsTrigger>
            </TabsList>
          </Tabs>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="py-10 text-center text-[#5C6B64] text-xs"><Loader2 className="w-4 h-4 mx-auto animate-spin" /></div>
          ) : filtered.length === 0 ? (
            <div className="py-12 text-center text-[#5C6B64] text-xs" data-testid="empty-state">
              No vouchers {tab === 'all' ? 'yet' : `in '${STATUS_META[tab]?.label || tab}'`}.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm text-left text-[#1A2421]">
                <thead>
                  <tr className="text-[10px] uppercase tracking-wider text-[#5C6B64] bg-[#F9F9F8] border-b border-[#E2E8DF]">
                    <th className="px-3 py-2 font-medium w-[160px]">Voucher No.</th>
                    <th className="px-3 py-2 font-medium">Outlet</th>
                    <th className="px-3 py-2 font-medium">Paid To</th>
                    <th className="px-3 py-2 font-medium text-right">Amount</th>
                    <th className="px-3 py-2 font-medium">Status</th>
                    <th className="px-3 py-2 font-medium">Created</th>
                    <th className="px-3 py-2 font-medium">Last Action</th>
                    <th className="px-3 py-2 font-medium text-right w-[200px]">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map(v => {
                    const meta = STATUS_META[v.status];
                    // STRICT: only the original preparer (manager) can edit.
                    // Approvers review only — they cannot edit voucher data.
                    const isCreator = v.prepared_by?.email === user?.email;
                    const canEdit = (v.status === 'pending' || v.status === 'needs_info') && isCreator;
                    // Send-to-accounts is a manager-side workflow; approvers
                    // approve and step out. We allow the creator + supervisors
                    // to send. Approvers (admin / area_manager / super_admin)
                    // cannot send.
                    const canSend = v.status === 'approved' && !canApprove && (isCreator || isManager);
                    const lastIso = v.approved_at || v.responded_at || v.info_requested_at || v.updated_at || v.created_at;
                    const lastLabel = v.status === 'approved' ? 'Approved'
                      : v.status === 'rejected' ? 'Rejected'
                      : v.status === 'needs_info' ? 'Info requested'
                      : v.responded_at ? 'Resubmitted'
                      : 'Updated';
                    return (
                      <tr key={v.id} className="border-b border-[#E2E8DF] last:border-b-0 hover:bg-[#F9F9F8]/60 cursor-pointer"
                        onClick={() => setDetail(v)} data-testid={`voucher-row-${v.id}`}>
                        <td className="px-3 py-2">
                          <span
                            className="inline-block font-mono text-[12px] font-semibold text-[color:var(--color-primary)] bg-[color:var(--color-primary)]/[0.08] border border-[color:var(--color-primary)]/20 rounded px-2 py-0.5 tracking-tight"
                            data-testid={`voucher-no-${v.id}`}
                            title="Voucher Number">
                            {v.voucher_no}
                          </span>
                        </td>
                        <td className="px-3 py-2 text-[12px]">{v.outlet_name}</td>
                        <td className="px-3 py-2 text-[12px]">{v.paid_to}</td>
                        <td className="px-3 py-2 text-right font-semibold tabular-nums">{fmt(v.amount)}</td>
                        <td className="px-3 py-2"><Badge className={`${meta.color} text-[10px]`}>{meta.label}</Badge>
                          {v.status === 'needs_info' && v.info_request_note && (
                            <p className="text-[10px] text-amber-800 mt-0.5 max-w-[180px] truncate"
                              title={v.info_request_note} data-testid={`info-note-preview-${v.id}`}>
                              <em>"{v.info_request_note}"</em>
                            </p>
                          )}
                        </td>
                        <td className="px-3 py-2 text-[11px] text-[#5C6B64] tabular-nums" title={v.created_at}>{relTime(v.created_at)}</td>
                        <td className="px-3 py-2 text-[11px] tabular-nums" title={`${lastLabel}: ${lastIso || '—'}`}>
                          <div className="flex flex-col leading-tight">
                            <span className="text-[#1A2421]">{relTime(lastIso)}</span>
                            <span className="text-[9px] uppercase tracking-wider text-[#9CA3AF]">{lastLabel}</span>
                          </div>
                        </td>
                        <td className="px-3 py-2 text-right" onClick={e => e.stopPropagation()}>
                          <div className="flex items-center gap-1 justify-end flex-wrap">
                            {canEdit && (
                              <Button size="sm" variant={v.status === 'needs_info' ? 'default' : 'ghost'}
                                className={`h-7 text-[11px] px-2 gap-1 ${v.status === 'needs_info' ? 'bg-amber-500 hover:bg-amber-600 text-white' : 'text-[#5C6B64]'}`}
                                onClick={() => { setEditing(v); setCreating(true); }} data-testid={`edit-${v.id}`}>
                                <Edit3 className="w-3 h-3" /> {v.status === 'needs_info' ? 'Respond' : 'Edit'}
                              </Button>
                            )}
                            {canApprove && v.status === 'pending' && (
                              <>
                                <Button size="sm" className="h-7 text-[11px] px-2 gap-1 bg-emerald-600 hover:bg-emerald-700 text-white"
                                  onClick={() => approve(v)} data-testid={`approve-${v.id}`}>
                                  <Check className="w-3 h-3" /> Approve
                                </Button>
                                <Button size="sm" variant="outline" className="h-7 text-[11px] px-2 gap-1 border-rose-200 text-rose-700 hover:bg-rose-50"
                                  onClick={() => { setActionTarget(v); setActionMode('reject'); }} data-testid={`reject-${v.id}`}>
                                  <X className="w-3 h-3" /> Reject
                                </Button>
                                <Button size="sm" variant="outline" className="h-7 text-[11px] px-2 gap-1 border-sky-200 text-sky-700 hover:bg-sky-50"
                                  onClick={() => { setActionTarget(v); setActionMode('request-info'); }} data-testid={`req-info-${v.id}`}>
                                  <AlertCircle className="w-3 h-3" /> Info
                                </Button>
                              </>
                            )}
                            {v.status === 'approved' && canSend && (
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button size="sm" className="h-7 text-[11px] px-2 gap-1 bg-[color:var(--color-primary)] hover:bg-[color:var(--color-primary-hover)] text-white" data-testid={`send-${v.id}`}>
                                    <Send className="w-3 h-3" /> Send <ChevronDown className="w-3 h-3" />
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end">
                                  <DropdownMenuItem onClick={() => downloadPdf(v)} data-testid={`pdf-${v.id}`}><Download className="w-3.5 h-3.5 mr-2" /> Download PDF</DropdownMenuItem>
                                  <DropdownMenuItem onClick={() => sendEmail(v)} data-testid={`email-${v.id}`}><Mail className="w-3.5 h-3.5 mr-2" /> Email Accounts</DropdownMenuItem>
                                  <DropdownMenuItem onClick={() => sendWhatsApp(v)} data-testid={`wa-${v.id}`}><MessageCircle className="w-3.5 h-3.5 mr-2" /> WhatsApp Accounts</DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                            )}
                            {/* ITER239 — admin-only Void. Allowed on pending,
                                needs_info, and approved. Blocked on rejected
                                / voided (terminal). */}
                            {canApprove && ['pending', 'needs_info', 'approved'].includes(v.status) && (
                              <Button size="sm" variant="outline"
                                className="h-7 text-[11px] px-2 gap-1 border-rose-200 text-rose-700 hover:bg-rose-50"
                                onClick={() => setVoidTarget(v)} data-testid={`void-${v.id}`}>
                                <Ban className="w-3 h-3" /> Void
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
      {hasMore && (
        <div className="flex justify-center py-3">
          <Button
            variant="outline"
            size="sm"
            disabled={loadingMore}
            onClick={loadMore}
            data-testid="petty-cash-load-more"
          >
            {loadingMore ? (<><Loader2 className="w-3.5 h-3.5 mr-2 animate-spin" /> Loading…</>) : 'Load more'}
          </Button>
        </div>
      )}

      <VoucherFormDialog
        open={creating}
        onClose={() => { setCreating(false); setEditing(null); }}
        outlets={outlets}
        defaultOutletId={user?.outlet_id}
        editing={editing}
        onSaved={reload}
      />
      <ActionDialog
        open={!!actionMode}
        mode={actionMode}
        voucher={actionTarget}
        onClose={() => { setActionMode(null); setActionTarget(null); }}
        onDone={reload}
      />
      <VoidDialog
        open={!!voidTarget}
        voucher={voidTarget}
        onClose={() => setVoidTarget(null)}
        onDone={reload}
      />
      {detail && <VoucherDetailDialog voucher={detail} onClose={() => setDetail(null)} />}
    </div>
  );
}

// ─── Indian-style amount in words (preview only — backend re-computes) ─
function amountToWordsIndian(num) {
  if (num === 0) return 'Indian Rupees Zero Only';
  const a = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine',
    'Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
  const b = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
  const inWords = (n) => {
    if (n < 20) return a[n];
    if (n < 100) return b[Math.floor(n / 10)] + (n % 10 ? ' ' + a[n % 10] : '');
    if (n < 1000) return a[Math.floor(n / 100)] + ' Hundred' + (n % 100 ? ' ' + inWords(n % 100) : '');
    if (n < 100000) return inWords(Math.floor(n / 1000)) + ' Thousand' + (n % 1000 ? ' ' + inWords(n % 1000) : '');
    if (n < 10000000) return inWords(Math.floor(n / 100000)) + ' Lakh' + (n % 100000 ? ' ' + inWords(n % 100000) : '');
    return inWords(Math.floor(n / 10000000)) + ' Crore' + (n % 10000000 ? ' ' + inWords(n % 10000000) : '');
  };
  return `Indian Rupees ${inWords(num)} Only`;
}
